import React, { useState, useEffect } from 'react';
import { 
  Typography, Box, Paper, TextField, Button, CircularProgress, Alert,
  FormControlLabel, Switch, Grid, FormControl, InputLabel, Select, MenuItem,
  Chip, OutlinedInput, Checkbox, ListItemText
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import api from '../../services/api';

// All possible status options
const ALL_STATUS_OPTIONS = [
  'Normal', 
  'Overdue', 
  'Over Exposure', 
  'Blacklisted', 
  'Defaulter'
];

const AssociationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id);
  const [error, setError] = useState(null);

  const validationSchema = Yup.object({
    name: Yup.string().required('Association name is required'),
    industry: Yup.string().nullable(),
    city: Yup.string().nullable(),
    description: Yup.string().nullable(),
    is_active: Yup.boolean(),
    expiry_date: Yup.date().nullable(),
    allowed_status: Yup.array()
      .of(Yup.string())
      .min(1, 'At least one status must be selected')
      .required('Allowed statuses are required'),
    default_status: Yup.string()
      .required('Default status is required')
      .test(
        'is-allowed-status',
        'Default status must be one of the allowed statuses',
        function(value) {
          return this.parent.allowed_status.includes(value);
        }
      )
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      industry: '',
      city: '',
      description: '',
      is_active: true,
      expiry_date: null,
      allowed_status: ['Normal', 'Defaulter'], // Default to Normal and Defaulter
      default_status: 'Normal' // Default status is "Normal"
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);
        
        // Format date for API
        const formattedValues = {
          ...values,
          expiry_date: values.expiry_date ? values.expiry_date.toISOString().split('T')[0] : null
        };
        
        if (id) {
          // Update existing association
          await api.put(`/api/admin/associations/${id}`, formattedValues);
          toast.success('Association updated successfully');
        } else {
          // Create new association
          await api.post('/api/admin/associations', formattedValues);
          toast.success('Association created successfully');
        }
        
        // Navigate back to associations list
        navigate('/admin/associations');
      } catch (error) {
        console.error('Error saving association:', error);
        setError(error.response?.data?.error || 'Failed to save association');
        toast.error('Failed to save association');
      } finally {
        setLoading(false);
      }
    }
  });

  // Fetch association data if editing existing one
  useEffect(() => {
    const fetchAssociation = async () => {
      try {
        setInitialLoading(true);
        const response = await api.get(`/api/admin/associations/${id}`);
        const association = response.data;
        
        // Parse dates
        const parsedData = {
          ...association,
          expiry_date: association.expiry_date ? new Date(association.expiry_date) : null
        };
        
        formik.setValues(parsedData);
      } catch (error) {
        console.error('Error fetching association:', error);
        setError('Failed to load association data');
        toast.error('Failed to load association data');
      } finally {
        setInitialLoading(false);
      }
    };
    
    if (id) {
      fetchAssociation();
    }
  }, [id]);

  const handleBack = () => {
    navigate('/admin/associations');
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={4}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4">{id ? 'Edit Association' : 'Create Association'}</Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Association Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                disabled={loading}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="industry"
                name="industry"
                label="Industry"
                value={formik.values.industry || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.industry && Boolean(formik.errors.industry)}
                helperText={formik.touched.industry && formik.errors.industry}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="city"
                name="city"
                label="City"
                value={formik.values.city || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expiry Date"
                  value={formik.values.expiry_date}
                  onChange={(date) => formik.setFieldValue('expiry_date', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: formik.touched.expiry_date && Boolean(formik.errors.expiry_date),
                      helperText: formik.touched.expiry_date && formik.errors.expiry_date
                    }
                  }}
                  disabled={loading}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="description"
                name="description"
                label="Description"
                multiline
                rows={4}
                value={formik.values.description || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl 
                fullWidth 
                error={formik.touched.allowed_status && Boolean(formik.errors.allowed_status)}
              >
                <InputLabel id="allowed-status-label">Allowed Customer Statuses</InputLabel>
                <Select
                  labelId="allowed-status-label"
                  id="allowed_status"
                  multiple
                  value={formik.values.allowed_status}
                  onChange={(e) => {
                    const newAllowedStatus = e.target.value;
                    formik.setFieldValue('allowed_status', newAllowedStatus);
                    
                    // If default status is not in the new allowed status list, update it to the first allowed status
                    if (!newAllowedStatus.includes(formik.values.default_status) && newAllowedStatus.length > 0) {
                      formik.setFieldValue('default_status', newAllowedStatus[0]);
                    }
                  }}
                  input={<OutlinedInput id="select-allowed-status" label="Allowed Customer Statuses" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                  disabled={loading}
                >
                  {ALL_STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={formik.values.allowed_status.indexOf(status) > -1} />
                      <ListItemText primary={status} />
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.allowed_status && formik.errors.allowed_status ? (
                  <Typography color="error" variant="caption">
                    {formik.errors.allowed_status}
                  </Typography>
                ) : null}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl 
                fullWidth 
                error={formik.touched.default_status && Boolean(formik.errors.default_status)}
              >
                <InputLabel id="default-status-label">Default Status for New Customers</InputLabel>
                <Select
                  labelId="default-status-label"
                  id="default_status"
                  name="default_status"
                  value={formik.values.default_status}
                  onChange={(e) => {
                    formik.setFieldValue('default_status', e.target.value);
                  }}
                  label="Default Status for New Customers"
                  disabled={loading || formik.values.allowed_status.length === 0}
                >
                  {formik.values.allowed_status.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.default_status && formik.errors.default_status ? (
                  <Typography color="error" variant="caption">
                    {formik.errors.default_status}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                  This status will be automatically assigned to new customers
                </Typography>
              </FormControl>
            </Grid>

            {id && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.is_active}
                      onChange={(e) => formik.setFieldValue('is_active', e.target.checked)}
                      name="is_active"
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label="Active"
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={loading}
                  sx={{ mr: 2 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : id ? 'Update' : 'Create'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default AssociationForm;